// pages/weekly/weeklychecklist.create.jsx
// 📍 Complete with locationId

import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux"; // ✅ ADD
import {
  Input,
  Button,
  List,
  Spin,
  Card,
  Switch,
  Flex,
  Popover,
  Slider,
} from "antd";
import {
  useCreateChecklistMutation,
  useUpdateChecklistMutation,
  useFetchChecklistsByTypeQuery,
  useDeleteChecklistMutation,
  useReorderTasksMutation,
} from "../opencheck/checklists.service";
import { CheckOutlined, DeleteOutlined, MoreOutlined } from "@ant-design/icons";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { quillFormats, quillModules } from "../../constant";
import ReactQuill from "react-quill";
import "./style.css";

const WeeklycheckCreate = () => {
  const { t, i18n } = useTranslation();
  const curLanguage = i18n.language;
  const navigate = useNavigate();
  const user = useSelector((state) => state.app.user); // ✅ ADD

  // ✅ Get locationId
  const locationId = user?.activeLocationId || user?.restaurantId;

  const [openPopoverId, setOpenPopoverId] = useState(null);
  const [newChecklist, setNewChecklist] = useState("");
  const [isInput, setIsInput] = useState(false);
  const [record, setRecord] = useState(false);
  const [isTempSlider, setIsTempSlider] = useState(false);
  const [checklistData, setChecklistData] = useState([]);
  const [charCount, setCharCount] = useState(0);
  const quillRef = useRef(null);
  const CHARACTER_LIMIT = 1500;

  // ✅ Fetch with locationId
  const {
    data = { data: { checklists: [] } },
    isFetching,
    isSuccess,
  } = useFetchChecklistsByTypeQuery(
    { type: "weekly", locationId }, // ✅ CHANGE
    { refetchOnMountOrArgChange: true }
  );

  const {
    data: { checklists },
  } = data;

  const [createChecklist, { isLoading: isCreateLoading }] =
    useCreateChecklistMutation();
  const [updateChecklist] = useUpdateChecklistMutation();
  const [deleteChecklist] = useDeleteChecklistMutation();
  const [reorderTasks] = useReorderTasksMutation();

  const handleCompleteChecklist = async (data) => {
    try {
      await updateChecklist(data);
    } catch (error) {
      console.log("Failed to complete checklist");
    }
  };

  useEffect(() => {
    if (isSuccess) {
      const checklistTempData = checklists.reduce((arr, cur) => {
        arr.push({
          isSection: cur.isSection,
          _id: cur._id,
          taskNumber: cur.taskNumber,
          title: cur.isSection
            ? ""
            : cur["title"][curLanguage] || cur["title"]["default"],
          description: cur.isSection ? (
            <div
              style={{ paddingBottom: "10px" }}
              dangerouslySetInnerHTML={{
                __html: cur["title"][curLanguage] || cur["title"]["default"],
              }}
            />
          ) : (
            <div className="opening-check-add-task">
              {cur.isInput && (
                <div style={{ padding: "5px 15px" }}>
                  <Input style={{ background: "transparent" }} />
                </div>
              )}
              {cur.isTempSlider && (
                <>
                  <div
                    style={{
                      fontSize: "1.5rem",
                      textAlign: "center",
                      marginTop: 10,
                      fontWeight: "bold",
                    }}
                  >
                    {cur.tempSlider.toFixed(1)}°C
                  </div>
                  <Slider
                    style={{ margin: "0 15px" }}
                    styles={{
                      track: { background: "transparent" },
                      tracks: { background: "rgb(109, 179, 63)" },
                    }}
                    min={-30}
                    max={120}
                    step={0.1}
                    value={cur.tempSlider}
                  />
                </>
              )}
              {cur.isDateRecord && (
                <div>*RECORD TIME AND DATE AUTOMATICALLY*</div>
              )}
              {cur.status === "completed" ? (
                <div style={{ textAlign: "center", padding: "5px" }}>
                  <CheckOutlined className="checklist-complete" />
                </div>
              ) : (
                <div style={{ textAlign: "center", padding: "5px" }}>
                  <CheckOutlined
                    className="checklist-pending"
                    onClick={() =>
                      handleCompleteChecklist({
                        id: cur._id,
                        data: { status: "completed" },
                      })
                    }
                  />
                </div>
              )}
            </div>
          ),
        });
        return arr;
      }, []);
      setChecklistData(checklistTempData);
    }
  }, [checklists, curLanguage]);

  // ✅ Add task with locationId
  const handleAddTask = async () => {
    try {
      if (!newChecklist) {
        toast.error("Checklist title cannot be empty");
        return;
      }
      await createChecklist({
        title: newChecklist,
        type: "weekly",
        isInput: isInput,
        isDateRecord: record,
        isTempSlider: isTempSlider,
        isSection: false,
        locationId: locationId, // ✅ ADD
      });
      setNewChecklist("");
      setIsInput(false);
      setRecord(false);
      setIsTempSlider(false);
    } catch (error) {
      console.log("Failed to create checklist", error);
      toast.error("Failed to create checklist");
    }
  };

  // ✅ Add section with locationId
  const handleAddSection = async () => {
    try {
      if (!newChecklist) {
        toast.error("Checklist title cannot be empty");
        return;
      }
      await createChecklist({
        title: newChecklist,
        type: "weekly",
        isInput: false,
        isDateRecord: false,
        isTempSlider: false,
        isSection: true,
        locationId: locationId, // ✅ ADD
      });
      setNewChecklist("");
      setIsInput(false);
      setRecord(false);
      setIsTempSlider(false);
    } catch (error) {
      console.log("Failed to create section", error);
      toast.error("Failed to create section");
    }
  };

  // ✅ Delete with locationId
  const handleDeleteChecklist = async (id) => {
    try {
      await deleteChecklist({ id, locationId }).unwrap();
      toast.success("Checklist deleted successfully");
    } catch (error) {
      console.log("Failed to delete checklist", error);
      toast.error("Failed to delete checklist");
    }
  };

  // ✅ Reorder with locationId
  const handleReorderTask = (orderTask, type) => {
    if (type === "up" && orderTask.taskNumber !== 1) {
      const tmpTaskUp = checklists.find(
        (item) => item.taskNumber === orderTask.taskNumber - 1
      );
      if (tmpTaskUp) {
        const tmpDataUp = checklists.reduce((arr, cur) => {
          if (tmpTaskUp._id === cur._id) {
            arr.push({ id: cur._id, taskNumber: cur.taskNumber + 1 });
            return arr;
          }
          if (orderTask._id === cur._id) {
            arr.push({ id: cur._id, taskNumber: cur.taskNumber - 1 });
            return arr;
          }
          arr.push({ id: cur._id, taskNumber: cur.taskNumber });
          return arr;
        }, []);
        reorderTasks({ tasks: tmpDataUp, locationId: locationId });
      }
    }
    if (
      type === "down" &&
      checklists.filter((item) => item.taskNumber > orderTask.taskNumber)
        .length > 0
    ) {
      const tmpTaskDown = checklists.find(
        (item) => item.taskNumber === orderTask.taskNumber + 1
      );
      if (tmpTaskDown) {
        const tmpDataDown = checklists.reduce((arr, cur) => {
          if (tmpTaskDown._id === cur._id) {
            arr.push({ id: cur._id, taskNumber: cur.taskNumber - 1 });
            return arr;
          }
          if (orderTask._id === cur._id) {
            arr.push({ id: cur._id, taskNumber: cur.taskNumber + 1 });
            return arr;
          }
          arr.push({ id: cur._id, taskNumber: cur.taskNumber });
          return arr;
        }, []);
        reorderTasks({ tasks: tmpDataDown, locationId: locationId });
      }
    }
  };

  const handleChange = (html, delta, source, editor) => {
    const text = editor.getText();
    const textLength = text.trim().length;

    if (textLength <= CHARACTER_LIMIT) {
      setNewChecklist(html);
      setCharCount(textLength);
    } else {
      if (quillRef.current) {
        const quill = quillRef.current.getEditor();
        quill.root.innerHTML = newChecklist;
      }
    }
  };

  return (
    <Spin spinning={isFetching}>
      <Card
        title={t("WEEKLYCHECK CREATE")}
        style={{ fontSize: "20px" }}
        bordered={false}
        className="opencheck-create-page"
        extra={
          <Flex justify="center">
            <Button type="default" onClick={() => navigate("/weeklychecks")}>
              {t("BACK")}
            </Button>
          </Flex>
        }
      >
        <div className="opencheck-create-page-container">
          <ReactQuill
            ref={quillRef}
            theme="snow"
            value={newChecklist}
            onChange={handleChange}
            modules={quillModules}
            formats={quillFormats}
          />
          <div style={{ textAlign: "right", fontSize: "14px" }}>
            {charCount} / 1500
          </div>
          <div className="opencheck-create-page-container-switch">
            <h4>{t("ADD TEXTBOX")}</h4>
            <Switch value={isInput} onChange={setIsInput} />
          </div>
          <div className="opencheck-create-page-container-switch">
            <h4>{t("RECORD TIME & DATE")}</h4>
            <Switch value={record} onChange={(e) => setRecord(e)} />
          </div>
          <div className="opencheck-create-page-container-switch">
            <h4>{t("ADD TEMPERATURE SLIDER")}</h4>
            <Switch value={isTempSlider} onChange={(e) => setIsTempSlider(e)} />
          </div>
          <div className="opencheck-create-page-container-button">
            <Button
              color="green"
              variant="outlined"
              loading={isCreateLoading}
              onClick={handleAddTask}
            >
              {t("Add task to checklist")}
            </Button>
            <Button
              color="green"
              variant="outlined"
              loading={isCreateLoading}
              onClick={handleAddSection}
            >
              {t("Add Section")}
            </Button>
          </div>
        </div>

        <List
          className="opencheck-create-page-insight"
          itemLayout="horizontal"
          dataSource={checklistData}
          renderItem={(checklist, index) => (
            <List.Item
              className={
                checklist.isSection ? "background-section" : "background-task"
              }
              extra={
                <Popover
                  placement="bottom"
                  content={
                    <div className="opencheck-list-page-tools-popover">
                      <Button
                        color="cyan"
                        variant="solid"
                        style={{ background: "rgb(47, 45, 113)" }}
                        onClick={() => {
                          handleReorderTask(checklist, "up");
                          setOpenPopoverId(null);
                        }}
                      >
                        {t("MoveUp")}
                      </Button>
                      <Button
                        color="cyan"
                        variant="solid"
                        style={{ background: "rgb(47, 45, 113)" }}
                        onClick={() => {
                          handleReorderTask(checklist, "down");
                          setOpenPopoverId(null);
                        }}
                      >
                        {t("MoveDown")}
                      </Button>
                      <Button
                        variant="solid"
                        color="danger"
                        size="small"
                        icon={<DeleteOutlined />}
                        onClick={() => {
                          handleDeleteChecklist(checklist._id);
                          setOpenPopoverId(null);
                        }}
                      >
                        {t("DELETE")}
                      </Button>
                    </div>
                  }
                  open={openPopoverId === checklist._id}
                  onOpenChange={(visible) =>
                    setOpenPopoverId(visible ? checklist._id : null)
                  }
                  trigger="click"
                >
                  <MoreOutlined className="opencheck-list-page-tools-icon" />
                </Popover>
              }
            >
              <List.Item.Meta
                title={
                  checklist.title !== "" ? (
                    <div
                      className="compliance-checklist-content"
                      dangerouslySetInnerHTML={{ __html: checklist.title }}
                    />
                  ) : (
                    ""
                  )
                }
                description={checklist.description}
              />
            </List.Item>
          )}
        />
      </Card>
    </Spin>
  );
};

export default WeeklycheckCreate;